// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/verbaNoArquivo.ts

/**
 * O que o arquivo permite escolher sobre a verba. **Os textos são literais do contrato visual**,
 * e são a única explicação de em que moeda a coluna é lida.
 *
 * A tela não abre a planilha, e a conversão para o líquido é do motor, linha a linha.
 */

export type DestinoDaVerbaDoArquivo = 'USE_FILE_ALLOWANCE' | 'IDEAL_FOR_ALL' | 'LEAVE_BLANK';

export type DestinoDasLinhasSemVerba = 'FILL_WITH_IDEAL' | 'LEAVE_EMPTY';

/** `hasAllowanceColumn` é opcional: "não tem coluna" e "não houve resposta" são diferentes. */
export type LeituraPreviaDoArquivo = {
  recognizedProducts?: number | null;
  hasAllowanceColumn?: boolean | null;
  filledAllowances?: number | null;
  /** O motivo, quando o arquivo não pôde ser lido. */
  message?: string | null;
};

export type EstadoDaVerbaNoArquivo = {
  destino: DestinoDaVerbaDoArquivo;
  destinoDasVazias: DestinoDasLinhasSemVerba;
};

/** O inquilino e o autor não viajam daqui: o gateway os resolve pelo token. */
export type EscolhasDaVerbaNoArquivo = {
  sellinAllowanceDestination: DestinoDaVerbaDoArquivo;
  blankAllowanceDestination?: DestinoDasLinhasSemVerba;
};

export const TEXTO_DA_VERBA_NO_ARQUIVO = {
  CABECALHO: 'Verba de sell-in no arquivo',
  APOIO_SEM_COLUNA: 'Seu arquivo não traz coluna de verba.',
  /** O leitor do motor é POSICIONAL: olha a terceira célula do cabeçalho, com qualquer texto. */
  ONDE_A_COLUNA_DE_VERBA_VAI:
    'A verba é a terceira coluna do arquivo, depois do código e do preço. O título dela pode ser qualquer um — só não pode ficar em branco.',
  AVISO_DA_CONVERSAO:
    'A coluna de verba do arquivo é lida no custo de entrada — é o valor que o fornecedor concede na nota. O sistema converte para o seu custo líquido em cada item.',
  OPCAO_USAR_DO_ARQUIVO: 'Usar a verba do arquivo',
  DESCRICAO_USAR_DO_ARQUIVO: 'Mantém exatamente o que o fornecedor enviou.',
  OPCAO_IDEAL_PARA_TODOS: 'Calcular a verba ideal para todos',
  DESCRICAO_IDEAL_PARA_TODOS: 'Ignora a coluna do arquivo e sugere o valor que recompõe sua margem.',
  OPCAO_DEIXAR_EM_BRANCO: 'Deixar em branco',
  DESCRICAO_DEIXAR_EM_BRANCO: 'Sobe só os custos; a verba você resolve na tela.',
  SUB_OPCAO_PREENCHER: 'Preencher com a verba ideal calculada',
  SUB_OPCAO_DEIXAR_VAZIAS: 'Deixar vazias para eu preencher',
  /** A única redação deste arquivo que não vem do contrato visual. */
  LENDO_O_ARQUIVO: 'Lendo o arquivo...',
} as const;

const TITULO_DA_OPCAO: Record<DestinoDaVerbaDoArquivo, string> = {
  USE_FILE_ALLOWANCE: TEXTO_DA_VERBA_NO_ARQUIVO.OPCAO_USAR_DO_ARQUIVO,
  IDEAL_FOR_ALL: TEXTO_DA_VERBA_NO_ARQUIVO.OPCAO_IDEAL_PARA_TODOS,
  LEAVE_BLANK: TEXTO_DA_VERBA_NO_ARQUIVO.OPCAO_DEIXAR_EM_BRANCO,
};

const DESCRICAO_DA_OPCAO: Record<DestinoDaVerbaDoArquivo, string> = {
  USE_FILE_ALLOWANCE: TEXTO_DA_VERBA_NO_ARQUIVO.DESCRICAO_USAR_DO_ARQUIVO,
  IDEAL_FOR_ALL: TEXTO_DA_VERBA_NO_ARQUIVO.DESCRICAO_IDEAL_PARA_TODOS,
  LEAVE_BLANK: TEXTO_DA_VERBA_NO_ARQUIVO.DESCRICAO_DEIXAR_EM_BRANCO,
};

const TITULO_DA_SUB_OPCAO: Record<DestinoDasLinhasSemVerba, string> = {
  FILL_WITH_IDEAL: TEXTO_DA_VERBA_NO_ARQUIVO.SUB_OPCAO_PREENCHER,
  LEAVE_EMPTY: TEXTO_DA_VERBA_NO_ARQUIVO.SUB_OPCAO_DEIXAR_VAZIAS,
};

export const tituloDaOpcaoDaVerba = (destino: DestinoDaVerbaDoArquivo): string => TITULO_DA_OPCAO[destino];

export const descricaoDaOpcaoDaVerba = (destino: DestinoDaVerbaDoArquivo): string => DESCRICAO_DA_OPCAO[destino];

export const tituloDaOpcaoDasVazias = (destino: DestinoDasLinhasSemVerba): string => TITULO_DA_SUB_OPCAO[destino];

export const OPCOES_DAS_LINHAS_SEM_VERBA: DestinoDasLinhasSemVerba[] = ['FILL_WITH_IDEAL', 'LEAVE_EMPTY'];

/** A resposta de recusa vem com o motivo e sem os números, e não conta como leitura. */
export const aLeituraChegou = (leitura: LeituraPreviaDoArquivo | null | undefined): boolean =>
  leitura != null && !leitura.message && leitura.hasAllowanceColumn != null;

export const temColunaDeVerba = (leitura: LeituraPreviaDoArquivo | null | undefined): boolean =>
  aLeituraChegou(leitura) && Boolean(leitura?.hasAllowanceColumn);

export const totalReconhecido = (leitura: LeituraPreviaDoArquivo | null | undefined): number =>
  Math.max(leitura?.recognizedProducts ?? 0, 0);

/** Sem coluna não há preenchido, seja qual for o número que chegue. */
export const quantidadeComVerba = (leitura: LeituraPreviaDoArquivo | null | undefined): number => {
  if (!temColunaDeVerba(leitura)) return 0;

  return Math.min(Math.max(leitura?.filledAllowances ?? 0, 0), totalReconhecido(leitura));
};

export const quantidadeSemVerba = (leitura: LeituraPreviaDoArquivo | null | undefined): number => {
  if (!temColunaDeVerba(leitura)) return 0;

  return totalReconhecido(leitura) - quantidadeComVerba(leitura);
};

/** **É a coluna existir que decide, e não ela estar preenchida.** */
export const opcoesDaVerbaNoArquivo = (leitura: LeituraPreviaDoArquivo | null | undefined): DestinoDaVerbaDoArquivo[] =>
  temColunaDeVerba(leitura) ? ['USE_FILE_ALLOWANCE', 'IDEAL_FOR_ALL', 'LEAVE_BLANK'] : ['IDEAL_FOR_ALL', 'LEAVE_BLANK'];

/** Sem coluna o padrão é deixar em branco: o arquivo não trouxe intenção de verba. */
export const estadoInicialDaVerbaNoArquivo = (leitura: LeituraPreviaDoArquivo | null | undefined): EstadoDaVerbaNoArquivo => ({
  destino: temColunaDeVerba(leitura) ? 'USE_FILE_ALLOWANCE' : 'LEAVE_BLANK',
  destinoDasVazias: 'FILL_WITH_IDEAL',
});

/** Trocar de destino **esquece a resposta das vazias**, que sai da tela junto com a pergunta. */
export const escolherODestinoDaVerba = (estado: EstadoDaVerbaNoArquivo, destino: DestinoDaVerbaDoArquivo): EstadoDaVerbaNoArquivo => {
  if (destino === estado.destino) return estado;

  return { destino, destinoDasVazias: 'FILL_WITH_IDEAL' };
};

export const escolherODestinoDasVazias = (estado: EstadoDaVerbaNoArquivo, destinoDasVazias: DestinoDasLinhasSemVerba): EstadoDaVerbaNoArquivo => {
  if (destinoDasVazias === estado.destinoDasVazias) return estado;

  return { ...estado, destinoDasVazias };
};

export const mostraASubPergunta = (leitura: LeituraPreviaDoArquivo | null | undefined, estado: EstadoDaVerbaNoArquivo): boolean =>
  temColunaDeVerba(leitura) && estado.destino === 'USE_FILE_ALLOWANCE' && quantidadeSemVerba(leitura) > 0;

export const mostraOAvisoDaConversao = (leitura: LeituraPreviaDoArquivo | null | undefined): boolean => temColunaDeVerba(leitura);

export const textoDeApoioDaVerbaNoArquivo = (leitura: LeituraPreviaDoArquivo | null | undefined): string => {
  if (!temColunaDeVerba(leitura)) return TEXTO_DA_VERBA_NO_ARQUIVO.APOIO_SEM_COLUNA;

  return `Seu arquivo traz a verba preenchida em ${quantidadeComVerba(leitura)} de ${totalReconhecido(leitura)} produtos.`;
};

export const linhaDaLeituraDoArquivo = (leitura: LeituraPreviaDoArquivo | null | undefined): string => {
  const total = totalReconhecido(leitura);

  if (!temColunaDeVerba(leitura)) return `${total} produtos reconhecidos · sem coluna de verba`;

  return `${total} produtos reconhecidos · coluna de verba preenchida em ${quantidadeComVerba(leitura)} deles`;
};

export const rotuloDasLinhasSemVerba = (leitura: LeituraPreviaDoArquivo | null | undefined): string =>
  `E os ${quantidadeSemVerba(leitura)} produtos sem verba no arquivo?`;

/** Nada é "a leitura não voltou". O destino é conferido contra as opções daquele arquivo. */
export const escolhasDoEnvio = (
  leitura: LeituraPreviaDoArquivo | null | undefined,
  estado: EstadoDaVerbaNoArquivo,
): EscolhasDaVerbaNoArquivo | null => {
  if (!aLeituraChegou(leitura)) return null;

  const permitidas = opcoesDaVerbaNoArquivo(leitura);
  const destino = permitidas.includes(estado.destino) ? estado.destino : estadoInicialDaVerbaNoArquivo(leitura).destino;

  if (!mostraASubPergunta(leitura, { ...estado, destino })) return { sellinAllowanceDestination: destino };

  return { sellinAllowanceDestination: destino, blankAllowanceDestination: estado.destinoDasVazias };
};
